import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FileText, CheckCircle2, Clock, Download } from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AdminTemplateRequestsPage({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [notesById, setNotesById] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/template-requests`, { withCredentials: true });
      setRows(res.data?.requests || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load requests");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const markReady = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/template-requests/${id}/mark-ready`, {
        notes: (notesById[id] || "").trim(),
      }, { withCredentials: true });
      toast.success("User notified — template marked ready.");
      setNotesById(prev => { const n = { ...prev }; delete n[id]; return n; });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark ready");
    } finally { setBusyId(null); }
  };

  const pending = rows.filter(r => r.status === "pending");
  const completed = rows.filter(r => r.status === "ready");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="admin-template-requests-page">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Custom Template Requests</h1>
          <p className="text-sm text-gray-500 mt-1">Review user-submitted PPTX templates and mark them ready once configured.</p>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading...</p>
      ) : (
        <>
          <Section title={`Pending (${pending.length})`}>
            {pending.length === 0 && <Empty text="No pending submissions." />}
            {pending.map(r => (
              <Card key={r.id} className="border-gray-200 shadow-none" data-testid={`tr-pending-${r.id}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="truncate">{r.original_filename}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        From <span className="font-medium text-gray-700">{r.user_name || r.user_email}</span> · {relTime(r.submitted_at)} · {(r.size_bytes / 1024 / 1024).toFixed(1)} MB
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 flex-shrink-0">
                      <Clock className="w-3.5 h-3.5" />Pending
                    </span>
                  </div>
                  <Textarea
                    placeholder="Optional notes for the user..."
                    value={notesById[r.id] || ""}
                    onChange={(e) => setNotesById(prev => ({ ...prev, [r.id]: e.target.value }))}
                    className="mt-3 min-h-[60px] text-sm"
                    data-testid={`tr-notes-${r.id}`}
                  />
                  <div className="flex justify-end mt-3">
                    <Button
                      onClick={() => markReady(r.id)}
                      disabled={busyId === r.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`tr-mark-ready-${r.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      {busyId === r.id ? "Notifying..." : "Mark ready & notify"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </Section>

          <Section title={`Completed (${completed.length})`}>
            {completed.length === 0 && <Empty text="None yet." />}
            {completed.map(r => (
              <Card key={r.id} className="border-gray-200 shadow-none" data-testid={`tr-ready-${r.id}`}>
                <CardContent className="pt-3 pb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-700 truncate max-w-[260px]">{r.original_filename}</span>
                    <span className="text-xs text-gray-400">{r.user_email}</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                    <CheckCircle2 className="w-3.5 h-3.5" />Ready · {relTime(r.ready_at)}
                  </span>
                </CardContent>
              </Card>
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</h2>
    {children}
  </div>
);

const Empty = ({ text }) => (
  <p className="text-sm text-gray-400 py-3">{text}</p>
);
